import "@/configs/amplify";
import {
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  signIn,
  signOut,
} from "aws-amplify/auth";
import { apiUrl } from "@/lib/aicheck/api-base";
import { useAuthStore } from "@/stores/auth";

export type AuthErrorType = "newPasswordRequired" | "authenticationFailed";

export class AuthError extends Error {
  errorType: AuthErrorType;

  constructor(message: string, errorType: AuthErrorType) {
    super(message);
    this.errorType = errorType;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Amplify が「既にサインイン済み」を理由に signIn を拒否したか判定する。
 * v6 は UserAlreadyAuthenticatedException / "There is already a signed in user."
 * を返す。名前は minify で失われうるのでメッセージも見る。
 */
function isAlreadySignedInError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return (
    err.name === "UserAlreadyAuthenticatedException" ||
    /already .*signed in user/i.test(err.message)
  );
}

/** 残存セッションを破棄する。失敗しても握り潰す（ログイン継続を優先）。 */
async function discardLingeringSession(): Promise<void> {
  try {
    await signOut();
  } catch {
    // revoke 通信が失敗してもローカルトークンは Amplify 側で破棄される。
  }
  useAuthStore.getState().clearState();
}

async function attemptSignIn(email: string, password: string) {
  const result = await signIn({ username: email, password });

  if (
    result.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
  ) {
    throw new AuthError("Password change required", "newPasswordRequired");
  }

  return result;
}

export const authService = {
  async signIn(email: string, password: string) {
    // サインアウトは「その時点で Amplify に設定されている User Pool」のトークンしか
    // 消さない。環境切替（STG/Prod）を挟むと別プールのトークンが残り、次のログインが
    // UserAlreadyAuthenticatedException で弾かれる。サインアウトの revoke 通信が
    // 失敗した場合や、サインアウト完了前に再ログインした場合も同様。
    // そのため、ログイン前に残存セッションがあれば必ず破棄する。
    try {
      await getCurrentUser();
      await discardLingeringSession();
    } catch {
      // 未サインイン（正常系）。そのままログインへ進む。
    }

    try {
      return await attemptSignIn(email, password);
    } catch (err: unknown) {
      if (err instanceof AuthError) throw err;

      // 上の事前チェックをすり抜けた場合の最後の保険。1 度だけやり直す。
      if (isAlreadySignedInError(err)) {
        await discardLingeringSession();
        try {
          return await attemptSignIn(email, password);
        } catch (retryErr: unknown) {
          if (retryErr instanceof AuthError) throw retryErr;
          throw new AuthError(
            retryErr instanceof Error
              ? retryErr.message
              : "Authentication failed",
            "authenticationFailed",
          );
        }
      }

      throw new AuthError(
        err instanceof Error ? err.message : "Authentication failed",
        "authenticationFailed",
      );
    }
  },

  async signOut() {
    sessionStorage.setItem("manualSignOut", "1");
    try {
      await signOut();
    } catch (err) {
      // revoke 通信が失敗してもサインアウトは続行する。ここで throw すると
      // 呼び出し元（ツールバー）がログイン画面へ遷移できず、画面に留まってしまう。
      // 残存トークンは次回ログイン時に signIn 側で破棄される。
      console.error("Sign-out revoke failed; continuing sign-out", err);
    } finally {
      useAuthStore.getState().clearState();
    }
  },

  async isAuthenticated(): Promise<boolean> {
    try {
      const session = await fetchAuthSession();
      if (!session.tokens?.idToken) {
        return false;
      }
      const user = await getCurrentUser();
      return !!user;
    } catch {
      return false;
    }
  },

  async getIdToken(): Promise<string | null> {
    const attempts = [
      { forceRefresh: false, delayMs: 0 },
      { forceRefresh: true, delayMs: 100 },
      { forceRefresh: true, delayMs: 350 },
    ];

    for (const attempt of attempts) {
      try {
        if (attempt.delayMs > 0) await sleep(attempt.delayMs);
        await getCurrentUser();
        const session = await fetchAuthSession({
          forceRefresh: attempt.forceRefresh,
        });
        const token = session.tokens?.idToken;
        if (token) return token.toString();
      } catch {
        // Try the next refresh strategy.
      }
    }

    return null;
  },

  async getUserName(): Promise<string | null> {
    try {
      const attributes = await fetchUserAttributes();
      return attributes.name ?? attributes.email ?? null;
    } catch {
      return null;
    }
  },

  async loadXperisusUser() {
    try {
      const token = await authService.getIdToken();
      // 未認証時に送ると別オリジン API の ALB が CORS ヘッダ無しの 401 を返す。送らず抜ける。
      if (!token) throw new Error("未認証です。再度サインインしてください。");
      const res = await fetch(apiUrl("/api/xperisus/users/exists"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: "{}",
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data) useAuthStore.getState().updateXperisusUser(data);
    } catch {
      // ignore
    }
  },
};
